import React, { useEffect, useState } from "react";
import { adminApi, SOURCE_META } from "./api";
import {
  Users, TrendingUp, TrendingDown, Loader2, ArrowRight, MapPin, GraduationCap,
  Filter, Clock, UserX, AlertTriangle,
} from "lucide-react";

const Sparkline = ({ data, color }) => {
  const max = Math.max(1, ...data);
  const w = 90, h = 26;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - (v / max) * h}`).join(" ");
  return (
    <svg width={w} height={h} className="overflow-visible">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};

const TrendChart = ({ data }) => {
  const max = Math.max(1, ...data.map((d) => d.count));
  const w = 620, h = 120;
  const step = w / Math.max(1, data.length - 1);
  const pts = data.map((d, i) => `${i * step},${h - (d.count / max) * (h - 10)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" style={{ height: 140 }} preserveAspectRatio="none">
      <polyline points={pts} fill="none" stroke="#7c3aed" strokeWidth="2.5" />
      {data.map((d, i) => (
        <circle key={i} cx={i * step} cy={h - (d.count / max) * (h - 10)} r="2" fill="#7c3aed" />
      ))}
    </svg>
  );
};

const Trend = ({ pct }) => {
  if (pct === null || pct === undefined) return <span className="text-[12px] text-slate-400">—</span>;
  const up = pct >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[12px] font-semibold ${up ? "text-emerald-600" : "text-rose-500"}`}>
      {up ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}{Math.abs(pct)}%
    </span>
  );
};

export default function Dashboard({ goLeads }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    adminApi.dashboard().then(setData).catch(() => setErr(true));
  }, []);

  if (err) return <div className="p-8 text-slate-500">Could not load dashboard.</div>;
  if (!data) return <div className="grid place-items-center py-24"><Loader2 className="h-7 w-7 animate-spin text-violet-500" /></div>;

  const { totals, sources, trend, top_cities, grades, funnel, quick } = data;
  const maxSource = Math.max(1, ...sources.map((s) => s.count));
  const maxFunnel = Math.max(1, ...funnel.map((f) => f.count));

  const totalCards = [
    { label: "Total leads", value: totals.total, trend: null },
    { label: "Today", value: totals.today, trend: totals.today_trend },
    { label: "This week", value: totals.week, trend: totals.week_trend },
    { label: "This month", value: totals.month, trend: totals.month_trend },
  ];

  const chips = [
    { key: "new_today", label: "New today", icon: Filter, filters: { status: "New", quick: "new_today" }, count: quick.new_today },
    { key: "school_new", label: "Unread school inquiries", icon: Users, filters: { source: "school_inquiry", status: "New" }, count: quick.school_new },
    { key: "unassigned", label: "Unassigned", icon: UserX, filters: { assigned: "__none__" }, count: quick.unassigned },
    { key: "stuck_new", label: "Stuck in New 2+ days", icon: AlertTriangle, filters: { status: "New", quick: "stuck_new" }, count: quick.stuck_new },
  ];

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Totals strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {totalCards.map((c) => (
          <div key={c.label} className="rounded-2xl bg-white border border-slate-200 p-4" data-testid={`stat-${c.label.replace(/\s+/g, "-").toLowerCase()}`}>
            <p className="text-[12px] font-semibold uppercase tracking-wide text-slate-400">{c.label}</p>
            <div className="mt-1 flex items-end justify-between">
              <p className="text-3xl font-extrabold text-slate-900">{c.value}</p>
              <Trend pct={c.trend} />
            </div>
          </div>
        ))}
      </div>

      {/* Quick filter chips */}
      <div className="flex flex-wrap gap-2">
        {chips.map((c) => (
          <button key={c.key} onClick={() => goLeads(c.filters)} data-testid={`chip-${c.key}`}
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-[13px] font-semibold text-slate-700 hover:border-violet-300 hover:text-violet-700 transition-colors">
            <c.icon className="h-3.5 w-3.5" /> {c.label}
            <span className="ml-1 rounded-full bg-slate-100 px-1.5 text-[11px] font-bold">{c.count}</span>
          </button>
        ))}
      </div>

      {/* Source cards */}
      <div>
        <h3 className="text-[13px] font-bold uppercase tracking-wide text-slate-400 mb-3">Leads by source</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {sources.map((s) => {
            const meta = SOURCE_META[s.source] || { chip: "bg-slate-200 text-slate-700", dot: "#64748b" };
            return (
              <button key={s.source} onClick={() => goLeads({ source: s.source })} data-testid={`source-card-${s.source}`}
                className="group text-left rounded-2xl bg-white border border-slate-200 p-4 hover:shadow-lg hover:border-violet-200 transition-all">
                <div className="flex items-start justify-between">
                  <div>
                    <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: meta.dot }} />
                    <p className="mt-2 text-[13px] font-semibold text-slate-500">{s.label}</p>
                    <p className="text-3xl font-extrabold text-slate-900">{s.count}</p>
                  </div>
                  <div className="opacity-70"><Sparkline data={s.spark || []} color={meta.dot} /></div>
                </div>
                <span className="mt-2 inline-flex items-center gap-1 text-[12px] font-semibold text-violet-600 opacity-0 group-hover:opacity-100 transition-opacity">
                  View leads <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Bar breakdown */}
        <div className="lg:col-span-2 rounded-2xl bg-white border border-slate-200 p-5">
          <h3 className="text-[13px] font-bold uppercase tracking-wide text-slate-400 mb-4">Source breakdown</h3>
          <div className="space-y-2.5">
            {sources.map((s) => {
              const meta = SOURCE_META[s.source] || { dot: "#64748b" };
              return (
                <button key={s.source} onClick={() => goLeads({ source: s.source })} className="w-full flex items-center gap-3 group">
                  <span className="w-36 shrink-0 text-right text-[12px] font-semibold text-slate-600 truncate">{s.label}</span>
                  <span className="flex-1 h-6 rounded-md bg-slate-100 overflow-hidden">
                    <span className="block h-full rounded-md group-hover:opacity-80 transition-opacity" style={{ width: `${(s.count / maxSource) * 100}%`, background: meta.dot }} />
                  </span>
                  <span className="w-8 text-[13px] font-bold text-slate-800">{s.count}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Discover funnel */}
        <div className="rounded-2xl bg-white border border-slate-200 p-5">
          <h3 className="text-[13px] font-bold uppercase tracking-wide text-slate-400 mb-4">Discover funnel</h3>
          <div className="space-y-3">
            {funnel.map((f, i) => (
              <button key={f.step} onClick={() => goLeads({ source: "discover_assessment" })} className="w-full text-left" data-testid={`funnel-${i}`}>
                <div className="flex items-center justify-between text-[13px]">
                  <span className="font-semibold text-slate-700">{f.step}</span>
                  <span className="font-bold text-slate-900">{f.count}</span>
                </div>
                <div className="mt-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500" style={{ width: `${(f.count / maxFunnel) * 100}%` }} />
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Trend + cities + grades */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-2xl bg-white border border-slate-200 p-5">
          <h3 className="text-[13px] font-bold uppercase tracking-wide text-slate-400 mb-2">Leads per day (30 days)</h3>
          <TrendChart data={trend} />
        </div>
        <div className="space-y-4">
          <div className="rounded-2xl bg-white border border-slate-200 p-5">
            <h3 className="text-[13px] font-bold uppercase tracking-wide text-slate-400 mb-3 flex items-center gap-1.5"><MapPin className="h-4 w-4" /> Top cities</h3>
            {top_cities.length === 0 && <p className="text-[13px] text-slate-400">No city data yet.</p>}
            <div className="space-y-1.5">
              {top_cities.map((c) => (
                <button key={c.city} onClick={() => goLeads({ city: c.city })} className="w-full flex items-center justify-between text-[13px] hover:text-violet-700">
                  <span className="font-medium text-slate-700">{c.city}</span>
                  <span className="font-bold">{c.count}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="rounded-2xl bg-white border border-slate-200 p-5">
            <h3 className="text-[13px] font-bold uppercase tracking-wide text-slate-400 mb-3 flex items-center gap-1.5"><GraduationCap className="h-4 w-4" /> Discover by grade</h3>
            {grades.length === 0 && <p className="text-[13px] text-slate-400">No grade data yet.</p>}
            <div className="flex flex-wrap gap-2">
              {grades.map((g) => (
                <button key={g.grade} onClick={() => goLeads({ source: "discover_assessment", grade: g.grade })}
                  className="rounded-xl border border-slate-200 px-3 py-1.5 text-[13px] font-semibold text-slate-700 hover:border-violet-300 hover:text-violet-700">
                  Grade {g.grade} <span className="text-slate-400">·</span> {g.count}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
